import * as React from "react";
import * as RPNInput from "react-phone-number-input";
import { getCountryCallingCode, parsePhoneNumber } from "react-phone-number-input";
import type { Country } from "react-phone-number-input";

import { cn } from "../../lib/utils";
import { CountrySelect } from "./country-select";

/** Re-exported so callers can type an ISO-2 code without depending on `react-phone-number-input` directly. */
export type PhoneInputCountry = Country;

type PhoneInputProps = Omit<React.ComponentProps<"input">, "value" | "onChange" | "type"> & {
  value?: string;
  onChange?: (value: string) => void;
  defaultCountry?: Country;
};

type PhoneCountrySelectProps = {
  disabled?: boolean;
  value?: Country;
  onChange: (value?: Country) => void;
};

/** The number field — borderless: it lives inside the shared phone container, so the box styling is on the parent. */
const PhoneInputComponent = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      type='tel'
      autoComplete='tel'
      data-slot='phone-input'
      className={cn(
        "placeholder:text-muted-foreground text-input-foreground h-full w-full min-w-0 flex-1 bg-transparent px-2.5 text-base outline-none md:text-sm",
        className,
      )}
      {...props}
    />
  ),
);
PhoneInputComponent.displayName = "PhoneInputComponent";

function getCountryFromValue(value?: string): Country | undefined {
  if (!value) return undefined;

  try {
    return parsePhoneNumber(value)?.country;
  } catch {
    return undefined;
  }
}

function normalizePlaceholder(placeholder: string | undefined, country: Country): string | undefined {
  if (!placeholder) return placeholder;

  const countryCallingCode = `+${getCountryCallingCode(country)}`;
  if (!placeholder.startsWith(countryCallingCode)) return placeholder;

  return placeholder.slice(countryCallingCode.length).replace(/^[\s()-]+/, "") || placeholder;
}

const MAX_NATIONAL_DIGITS = 10;

export type PastedPhone = { country: Country; nationalNumber: string };

/**
 * Turn whatever was pasted into the number box into a country and the digits that belong in the box, so the
 * calling code the country picker already shows is never duplicated inside it.
 *
 * - `+243 812…` / `00243 812…`: international, so the number's own country wins (the picker follows it).
 * - `243812…` while +243 is selected: the calling code was copied along — drop it.
 * - more than 10 digits that read as a valid international number: take that country.
 * - otherwise: the national number for the selected country, keeping only the last 10 digits.
 */
export function resolvePastedPhone(text: string, selectedCountry: Country): PastedPhone | null {
  const trimmed = text.trim();
  let digits = trimmed.replace(/\D/g, "");
  if (!digits) return null;

  const international = trimmed.startsWith("+") || digits.startsWith("00");
  if (digits.startsWith("00")) digits = digits.slice(2);
  const selectedCode = getCountryCallingCode(selectedCountry);

  const asInternational = (): PastedPhone | null => {
    try {
      const parsed = parsePhoneNumber(`+${digits}`);
      if (!parsed?.country) return null;
      return { country: parsed.country, nationalNumber: parsed.nationalNumber.slice(-MAX_NATIONAL_DIGITS) };
    } catch {
      return null;
    }
  };

  if (international) {
    const resolved = asInternational();
    if (resolved) return resolved;
    if (digits.startsWith(selectedCode)) digits = digits.slice(selectedCode.length);
  } else if (digits.length > MAX_NATIONAL_DIGITS) {
    if (digits.startsWith(selectedCode)) {
      digits = digits.slice(selectedCode.length);
    } else {
      try {
        if (parsePhoneNumber(`+${digits}`)?.isValid()) return asInternational();
      } catch {
        // Not an international number; fall through to the national rules.
      }
    }
  }

  const national = digits.slice(-MAX_NATIONAL_DIGITS);
  try {
    // Drops a national trunk prefix (`0812…` → `812…`), the same way typing it into the field does.
    const parsed = parsePhoneNumber(national, selectedCountry);
    if (parsed) return { country: selectedCountry, nationalNumber: parsed.nationalNumber };
  } catch {
    // Too short to parse yet; keep the digits as they are.
  }
  return { country: selectedCountry, nationalNumber: national };
}

/** Put digits into the number box as if typed, so the library formats them nationally for its country. */
function typeIntoInput(input: HTMLInputElement, digits: string) {
  const setValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  setValue?.call(input, digits);
  input.dispatchEvent(new Event("input", { bubbles: true }));
}

/** The country picker: an inset prefix (flag + calling code + chevron) inside the pill, no box and no divider. */
function PhoneCountrySelect({ disabled, value, onChange }: PhoneCountrySelectProps) {
  return (
    <CountrySelect
      value={value}
      onChange={onChange}
      disabled={disabled}
      compact
      showCallingCode
      className='h-full rounded-none border-0 bg-transparent pr-1 pl-3 font-normal shadow-none hover:bg-muted/60 focus-visible:ring-0 dark:bg-transparent dark:hover:bg-muted/40'
    />
  );
}

/**
 * A single-control phone input: the country picker sits inside the field as a prefix (flag + calling code) and
 * the number formats as you type (an as-you-type mask via `react-phone-number-input`), so the whole thing reads
 * as one input rather than two boxes. The bound value is the full E.164 number.
 *
 * Country is left UNCONTROLLED (`defaultCountry`) — `react-phone-number-input` owns it and keeps it in sync with
 * the value. We do NOT also pass a controlled `country` or mirror it in local state: doing both made the library
 * fall back to international formatting, leaking the calling code into the number box and mangling typed digits.
 * The custom country select reads the library's own `selectProps.value`/`onChange`.
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, defaultCountry = "CD", onChange, onPaste, placeholder, value, ...props }, ref) => {
    // Only for trimming a redundant calling-code prefix from the placeholder — never to control the country.
    const placeholderCountry = getCountryFromValue(value) ?? defaultCountry;
    // The library's current country, read at paste time. Never fed back as a controlled `country`.
    const countryRef = React.useRef<Country>(defaultCountry);
    const setCountryRef = React.useRef<(country: Country) => void>(() => undefined);
    // A paste that switched country, waiting for the new country to render before its digits go in.
    const pendingPasteRef = React.useRef<{ field: HTMLElement | null; paste: PastedPhone } | null>(null);

    return (
      <RPNInput.default
        ref={ref as React.ForwardedRef<React.ElementRef<typeof RPNInput.default>>}
        className={cn(
          "border-border focus-within:border-ring focus-within:ring-ring/50 has-[input:disabled]:bg-input/70 h-control flex w-full min-w-0 items-stretch overflow-hidden rounded-[var(--control-radius)] border bg-background text-base shadow-[var(--shadow-field)] outline-none transition-colors focus-within:ring-3 md:text-sm dark:bg-input",
          className,
        )}
        defaultCountry={defaultCountry}
        countrySelectComponent={(selectProps) => {
          countryRef.current = selectProps.value ?? defaultCountry;
          setCountryRef.current = selectProps.onChange;
          const pending = pendingPasteRef.current;
          if (pending && pending.paste.country === selectProps.value) {
            pendingPasteRef.current = null;
            setTimeout(() => {
              const input = pending.field?.querySelector<HTMLInputElement>("input[data-slot='phone-input']");
              if (input) typeIntoInput(input, pending.paste.nationalNumber);
            });
          }
          return (
            <PhoneCountrySelect
              disabled={selectProps.disabled}
              // Reflect the library's own country (falling back to the default so the flag always shows).
              value={selectProps.value ?? defaultCountry}
              onChange={(nextCountry) => {
                if (!nextCountry) return;
                selectProps.onChange(nextCountry);
              }}
            />
          );
        }}
        inputComponent={PhoneInputComponent}
        smartCaret={false}
        onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => {
          onPaste?.(event);
          if (event.defaultPrevented) return;
          const resolved = resolvePastedPhone(event.clipboardData.getData("text"), countryRef.current);
          if (!resolved) return;
          event.preventDefault();
          const input = event.currentTarget;
          if (resolved.country === countryRef.current) {
            typeIntoInput(input, resolved.nationalNumber);
            return;
          }
          pendingPasteRef.current = { field: input.parentElement, paste: resolved };
          setCountryRef.current(resolved.country);
        }}
        placeholder={normalizePlaceholder(placeholder, placeholderCountry)}
        value={value || undefined}
        onChange={(nextValue) => {
          const next = nextValue ?? "";
          // Cap the national number at 10 digits (after the selected country's calling code) — reject extra typing.
          if (next.startsWith("+")) {
            const country = getCountryFromValue(next) ?? defaultCountry;
            const callingCode = getCountryCallingCode(country);
            const national = next.replace(/\D/g, "").slice(callingCode.length);
            if (national.length > 10) {
              onChange?.(`+${callingCode}${national.slice(0, 10)}`);
              return;
            }
          }
          onChange?.(next);
        }}
        {...props}
      />
    );
  },
);
PhoneInput.displayName = "PhoneInput";
